import { useMemo } from 'react'
import { map, reduce } from 'ramda'

import toFixedValue from 'app/common/lib/toFixedValue'

import { Allowance } from '../../../_common/useReportAllowances'
import { AllSchoolsAllowancesAndFoodModesDto } from '../../../_common/useReportAllSchoolsAllowancesAndFoodModesDto'
import { TotalsAllowancesAndFoodModesDto } from '../../../_common/useReportTotalsAllowancesAndFoodModesDto'
import { useSubsidyType } from '../../../_common/useSubsidyType'

import {
  ColumnKey,
  columnKeysLeft,
  columnKeysLeftHeaders,
  createColumnKeysMain,
  createColumnKeysMainHeaders,
  createHeaderCells,
  createFoodModesColumnKeys,
  createHeaderCellsProps,
  createCellsProps,
  createTotalCellsProps,
  columnKeysLeftHeadersNoSpan,
} from './tableColumns'

type Options = {
  allowances: Array<Allowance>
  allSchoolsAllowancesAndFoodModesDto: AllSchoolsAllowancesAndFoodModesDto
  totalsAllowancesAndFoodModesDto: TotalsAllowancesAndFoodModesDto
}

export function useByAllowancesAndFoodModesTableData({
  allowances,
  allSchoolsAllowancesAndFoodModesDto,
  totalsAllowancesAndFoodModesDto,
}: Options) {
  const subsidyType = useSubsidyType()

  const res = useMemo(() => {
    return {
      columnKeysLeft,
      columnKeysLeftHeaders: !subsidyType.showPersonal
        ? columnKeysLeftHeadersNoSpan
        : columnKeysLeftHeaders,
      columnKeysMain: createColumnKeysMain(allowances, subsidyType),
      columnKeysMainHeaders: createColumnKeysMainHeaders(allowances, subsidyType),
      header: createHeaderCells(allowances),
      headerProps: createHeaderCellsProps(allowances, subsidyType),
      data: map(
        ({ school_title: title, data: { common, total, allowances: byAllowance } }) => ({
          rowKey: title,
          rowData: reduce(
            (acc, allowance) => {
              const { Завтрак: breakfast, Обед: lunch, Полдник: afternoon } = byAllowance[allowance]
              const value1 = toFixedValue(breakfast)
              const value2 = toFixedValue(lunch)
              const value3 = toFixedValue(afternoon)
              const [key1, key2, key3] = createFoodModesColumnKeys(allowance)
              acc[key1] = { columnKey: key1, value: value1 }
              acc[key2] = { columnKey: key2, value: value2 }
              acc[key3] = { columnKey: key3, value: value3 }
              return acc
            },
            {
              [ColumnKey.Org]: { columnKey: ColumnKey.Org as const, value: title },
              [ColumnKey.Common]: {
                columnKey: ColumnKey.Common as const,
                value: toFixedValue(common),
              },
              [ColumnKey.Total]: {
                columnKey: ColumnKey.Total as const,
                value: toFixedValue(total),
              },
            },
            allowances,
          ),
        }),
        allSchoolsAllowancesAndFoodModesDto,
      ),
      dataProps: createCellsProps(allowances),
      total: [
        {
          rowKey: 'total',
          rowData: reduce(
            (acc, allowance) => {
              const {
                Завтрак: breakfast,
                Обед: lunch,
                Полдник: afternoon,
              } = totalsAllowancesAndFoodModesDto.allowances[allowance]
              const value1 = toFixedValue(breakfast)
              const value2 = toFixedValue(lunch)
              const value3 = toFixedValue(afternoon)
              const [key1, key2, key3] = createFoodModesColumnKeys(allowance)
              acc[key1] = { columnKey: key1, value: value1 }
              acc[key2] = { columnKey: key2, value: value2 }
              acc[key3] = { columnKey: key3, value: value3 }
              return acc
            },
            {
              [ColumnKey.Org]: {
                columnKey: ColumnKey.Org as const,
                value: 'Итого:',
              },
              [ColumnKey.Common]: {
                columnKey: ColumnKey.Common as const,
                value: toFixedValue(totalsAllowancesAndFoodModesDto.totalCommon),
              },
              [ColumnKey.Total]: {
                columnKey: ColumnKey.Total as const,
                value: toFixedValue(totalsAllowancesAndFoodModesDto.total),
              },
            },
            allowances,
          ),
        },
      ],
      totalProps: createTotalCellsProps(allowances),
    }
  }, [
    allowances,
    allSchoolsAllowancesAndFoodModesDto,
    totalsAllowancesAndFoodModesDto,
    subsidyType,
  ])

  return res
}
